import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import Logo from "@/components/Logo";
import {
  HERO,
  IMAGES,
  COMPETENCIES,
  CONTACT_INFO,
} from "@/data/content";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const logoScale = useTransform(scrollYProgress, [0, 1], [1, 0.82]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* ---------------- HERO ---------------- */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden grain px-5"
      >
        <motion.div style={{ y: bgY }} className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#0A0A0A]" />
        <motion.div
          style={{ y: bgY }}
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full bg-[#0055FF]/20 blur-[140px] pointer-events-none"
        />

        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative z-10 flex flex-col items-center text-center max-w-5xl mx-auto pt-24"
        >
          {/* Large centered logo */}
          <motion.div
            style={{ y: logoY, scale: logoScale }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-logo"
            className="mb-12 text-white"
          >
            <Logo className="text-[96px] sm:text-[150px] md:text-[190px] lg:text-[210px]" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-blue-400 mb-6 flex items-center gap-2"
          >
            <ShieldCheck size={14} /> {HERO.eyebrow}
          </motion.p>

          <h1 className="font-heading font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-7xl leading-[0.95]">
            {HERO.lines.map((line, i) => (
              <MaskLine key={i} delay={0.5 + i * 0.12}>
                {i === HERO.lines.length - 1 ? (
                  <span className="text-blue-500">{line}</span>
                ) : (
                  line
                )}
              </MaskLine>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-12 flex flex-wrap items-center justify-center gap-x-14 sm:gap-x-20 gap-y-6"
            data-testid="hero-identifiers"
          >
            {[
              { label: "CAGE Code", value: CONTACT_INFO.cage },
              { label: "UEI", value: CONTACT_INFO.uei },
              { label: "DUNS", value: CONTACT_INFO.duns },
              { label: "GSA", value: CONTACT_INFO.gsa },
            ].map((id) => (
              <div key={id.label} className="text-center">
                <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.28em] text-blue-400 mb-2.5">
                  {id.label}
                </p>
                <p className="font-mono text-xl sm:text-2xl md:text-3xl font-semibold text-white tracking-wider">
                  {id.value}
                </p>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group flex items-center gap-2 px-7 py-3.5 bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold uppercase tracking-wide text-sm transition-transform hover:scale-[1.04]"
            >
              Request a Consultation
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/about"
              data-testid="hero-cta-secondary"
              className="px-7 py-3.5 border border-white/25 hover:border-white text-white font-bold uppercase tracking-wide text-sm transition-colors"
            >
              Meet the Team
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* ---------------- MARQUEE ---------------- */}
      <section data-testid="marquee-section" className="py-10 border-y border-white/10 bg-[#0A0A0A]">
        <Marquee speed={40} gradient={false}>
          {COMPETENCIES.map((c, i) => (
            <span
              key={i}
              className="font-heading font-black uppercase text-4xl sm:text-6xl tracking-tight text-stroke mx-8 flex items-center gap-8"
            >
              {c}
              <span className="text-blue-500 text-3xl not-italic" style={{ WebkitTextStroke: 0 }}>
                ◆
              </span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* ---------------- STATS (removed) ---------------- */}

      {/* ---------------- "How we operate" moved to /capabilities ---------------- */}

      {/* ---------------- CERTIFICATIONS moved to /certifications ---------------- */}

      {/* ---------------- WHO WE SERVE (removed) ---------------- */}

      {/* ---------------- CTA BAND ---------------- */}
      <section
        data-testid="cta-band"
        className="relative overflow-hidden border-y border-white/10 grain"
        style={{
          backgroundImage: `linear-gradient(rgba(10,10,10,0.86), rgba(10,10,10,0.92)), url(${IMAGES.govBuilding})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-32 text-center relative z-10">
          <Reveal>
            <h2 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-5xl md:text-6xl leading-[0.95] max-w-4xl mx-auto">
              Have a facility requirement? <span className="text-blue-500">Let&apos;s build it.</span>
            </h2>
            <Link
              to="/contact"
              data-testid="cta-band-button"
              className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold uppercase tracking-wide text-sm transition-transform hover:scale-[1.04]"
            >
              Contact Our Team <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
